/**
 * Console language preference.
 *
 * Same cookie + HTML-attribute pattern as `theme.ts`: the inline boot script in
 * `index.html` applies `lang` / `dir` before the bundle paints, and this module
 * is the same logic for the app afterwards.
 */

export type Locale = "ar" | "en";

export const LOCALE_COOKIE = "teslm_locale";
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
export const DEFAULT_LOCALE: Locale = "ar";

let activeLocale: Locale = DEFAULT_LOCALE;

export function isLocale(value: string | undefined | null): value is Locale {
  return value === "ar" || value === "en";
}

export function getActiveLocale(): Locale {
  /* Prefer the live DOM attribute so Vite HMR cannot leave two module copies
     disagreeing about `activeLocale` while `document.documentElement.lang` is
     already correct. */
  if (typeof document !== "undefined") {
    const fromDom = document.documentElement.lang;
    if (isLocale(fromDom)) {
      activeLocale = fromDom;
      return fromDom;
    }
  }
  return activeLocale;
}

/**
 * Catalogue label for the active language.
 *
 * English is used when it is filled in. An empty English name stays on the
 * Arabic one, so a type that was never translated does not become a blank.
 */
export function localizedName(
  nameAr: string,
  nameEn: string | null | undefined,
  locale: Locale,
): string {
  if (locale === "en") {
    const english = nameEn?.trim();
    if (english) return english;
  }
  return nameAr;
}

export function applyLocale(locale: Locale): void {
  activeLocale = locale;
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
  document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
}

export function readLocale(): Locale {
  if (typeof document === "undefined") return activeLocale;

  if (isLocale(document.documentElement.lang)) {
    activeLocale = document.documentElement.lang;
    return activeLocale;
  }

  const entry = document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${LOCALE_COOKIE}=`));
  const value = entry?.slice(LOCALE_COOKIE.length + 1);
  const locale = isLocale(value) ? value : DEFAULT_LOCALE;
  activeLocale = locale;
  return locale;
}

export function writeLocale(locale: Locale): void {
  applyLocale(locale);
  if (typeof document === "undefined") return;
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`;
}

export function localeDir(locale: Locale): "rtl" | "ltr" {
  return locale === "ar" ? "rtl" : "ltr";
}

export function localeTag(locale: Locale): string {
  return locale === "ar" ? "ar-SA" : "en-SA";
}
